import { useParams } from "react-router-dom";
import { useAppSelector } from "store";
import Breadcrumbs from "components/common/Breadcrumbs";
import { useCrmNavigation } from "hooks/useCrmNavigation";
import { useContactOwners } from "service/crm";
import { selectAuthUser } from "store/slices/auth/auth.selector";
import ContactDetailsSkeleton from "components/Loaders/ContactDetailsSkeleton";
import { timeAgo } from "utils/date";
import { useCompanyDetailsByKey } from "service/crm/hooks/useCompanyDetails";
import CompanyDetailsCard from "components/modules/CRM/Companies/CompanyDetailsCard";
import CompanyAssociation from "components/modules/CRM/Companies/CompanyAssociation";
import CompanyActivity from "components/modules/CRM/Companies/CompanyActivity";
import ContactEmptyState from "components/modules/CRM/ContactEmptyState";
import { Building, Eye, Plus } from "lucide-react";
import { RecordAccessProvider } from "context/crm/RecordAccessContext";
import { Stat } from "components/common/StatCard";
import { formatUsdAmount } from "utils/helpers";

const CompanyDetails = () => {
    const authUser = useAppSelector(selectAuthUser);
    const { companyKey } = useParams<{ companyKey: string }>();
    const { company, isLoading, isError } = useCompanyDetailsByKey(companyKey ?? "");
    const { buildCompaniesUrl, buildViewContactUrl } = useCrmNavigation();


    const { data: owners = [] } = useContactOwners(
        authUser?.orgUuid as string,
    );

    const associationTimestamps = [
        company?.createdAt && {
            label: "Created",
            value: new Date(company.createdAt).toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            }),
        },
        company?.lastActivityAt && {
            label: "Last activity",
            value: timeAgo(company.lastActivityAt),
        },
    ].filter(Boolean) as { label: string; value: string }[];

    if (isLoading) return <ContactDetailsSkeleton />
    if (isError || !company) return <ContactEmptyState
        icon={Building}
        title="Company not found"
        description="The company you are looking for does not exist or has been deleted."
        action={{
            label: "New Company",
            icon: Plus,
            onClick: () => {

            },
        }}
        secondaryAction={{
            label: "View Companies",
            icon: Eye,
            onClick: () => { },
        }}
    />;

    // TODO: remove — debugging the USD deal value from the backend
    console.log("[Company] dealValue (USD) from API:", company.dealValue, {
        api: `GET /crm/companies/${company.uuid}`,
    });

    const overdueTasks = company.overdueTasks ?? 0;

    const stats: Stat[] = [
        {
            label: "Deal value",
            value: company.dealValue != null ? formatUsdAmount(company.dealValue) : "—",
            accent: "success",
        },
        {
            label: "Overdue tasks",
            value: overdueTasks,
            // Only flag it when there is something to act on.
            ...(overdueTasks > 0 ? { accent: "warning" as const } : {}),
        },
        { label: "Activities", value: company.activitiesCount ?? 0 },
        { label: "Notes", value: company.notesCount ?? 0 },
    ];


    return (
        /**
         * RBAC: resolves this company's access once — CRM Admin/Manager get full
         * access, a CRM User gets edit access only via `company.canEdit` (Owner,
         * assigned Task, or assigned Note on THIS company — section 18). The
         * details card, the shared Activity tree and the association panel all
         * read it via `useRecordAccess()`.
         *
         * Note this is the company's own access, independent of any associated
         * contact's (sections 20, 21).
         */
        <RecordAccessProvider record={company}>
            <div className='min-h-0 flex-1 flex flex-col text-white'>

                <Breadcrumbs
                    items={[
                        { label: 'Companies', path: buildCompaniesUrl() },
                        { label: `${companyKey}`, path: buildViewContactUrl(companyKey as string) },
                    ]}
                    activeLinkProps={{ className: 'text-neutral font-semibold' }}
                    className="px-6 shrink-0"
                />
                {/* ---------- Three column layout ---------- */}
                <div className="flex-1 min-h-0 bg-primary border-t border-primary-subtle mt-3 px-2">
                    <div className="grid grid-cols-1 md:grid-cols-12 items-start h-full min-h-0 lg:grid-rows-[minmax(0,1fr)]">
                        <div className="md:col-span-12 lg:col-span-3 space-y-6 lg:h-full lg:overflow-hidden">
                            <CompanyDetailsCard company={company} owners={owners} />
                        </div>

                        <div className="md:col-span-8 lg:col-span-6 space-y-6 lg:h-full lg:min-h-0 lg:overflow-y-auto">
                            <CompanyActivity companyUuid={company.uuid} />
                        </div>

                        <div className="md:col-span-12 lg:col-span-3 space-y-6 lg:h-full lg:min-h-0 lg:overflow-y-auto">
                            <CompanyAssociation companyUuid={company.uuid} stats={stats} timestamps={associationTimestamps} company={company} />
                        </div>
                    </div>
                </div>
            </div>
        </RecordAccessProvider>
    );
};


export default CompanyDetails